import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { SpaceMembership, SpaceRole } from '../types/spaces';

const KEY = (spaceId: string) => ['space', spaceId, 'members'] as const;

/**
 * List the active members of a space. Admin+ on the backend — the call 403s
 * for plain members/viewers. Subscribes to `optr.space.member.*` and
 * `optr.space.ownership-transferred` events filtered by spaceId so the list
 * reflects role changes and add/remove ops in real time.
 */
export function useMembers(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  const { subscribe } = usePlatformEvents();

  const query = useQuery({
    queryKey: KEY(spaceId ?? ''),
    queryFn: () => spaceApi(spaceId!).members.list(),
    enabled: !!spaceId,
  });

  useEffect(() => {
    if (!spaceId) return;
    const unsub = subscribe(
      [
        { eventType: 'optr.space.member.added', spaceId },
        { eventType: 'optr.space.member.removed', spaceId },
        { eventType: 'optr.space.member.role-changed', spaceId },
        { eventType: 'optr.space.ownership-transferred', spaceId },
      ],
      () => {
        queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
      },
    );
    return unsub;
  }, [spaceId, subscribe, queryClient]);

  return query;
}

export function useAddMember(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: 'admin' | 'member' | 'viewer' }) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).members.add(input);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

export function useUpdateMemberRole(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'admin' | 'member' | 'viewer';
    }) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).members.updateRole(userId, role);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

export function useRemoveMember(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).members.remove(userId);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

/** Pin an auto-joined member so auto-join rules no longer manage them. */
export function usePinMember(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).members.pin(userId);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

/** Sort helper: owner → admin → member → viewer, then by joinedAt. */
const ROLE_SORT_ORDER: Record<SpaceRole, number> = {
  owner: 0,
  admin: 1,
  member: 2,
  viewer: 3,
};
export function sortMembers(members: SpaceMembership[]): SpaceMembership[] {
  return [...members].sort((a, b) => {
    const r = ROLE_SORT_ORDER[a.role] - ROLE_SORT_ORDER[b.role];
    if (r !== 0) return r;
    return a.joinedAt.localeCompare(b.joinedAt);
  });
}

export function useSortedMembers(spaceId: string | undefined) {
  const q = useMembers(spaceId);
  const sorted = useMemo(() => sortMembers(q.data ?? []), [q.data]);
  return { ...q, data: sorted };
}
